import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Shield, LogOut, Edit2, Save, X, Phone, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';
import { useTenant } from '../context/TenantContext';

export default function Profile() {
    const { user, logout, setAuth } = useAuth();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    const toast = useToast();
    
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        fullName: user?.full_name || '',
        phone: user?.phone || ''
    });

    if (!user) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12 text-center">
                <p className="text-gray-500">Please login to view your profile.</p>
                <button 
                    onClick={() => navigate('/login')}
                    className="mt-4 btn-primary"
                >
                    Login Now
                </button>
            </div>
        );
    }

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.put('/api/auth/profile', formData);
            // Update local context
            const token = localStorage.getItem('token');
            setAuth(token, res.data.user);
            toast.success('Profile updated successfully');
            setIsEditing(false);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordUpdateReq = async () => {
        setLoading(true);
        try {
            await api.post('/api/auth/request-password-update-otp');
            toast.success('Verification code sent to your email');
            navigate('/verify-otp', { state: { email: user.email, type: 'password_update' } });
        } catch (err) {
            toast.error('Failed to initiate password update');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = window.location.origin;
    };

    return (
        <div className="max-w-3xl mx-auto px-4 py-8 pb-24 md:pb-8">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold">My Profile</h1>
                {!isEditing && (
                    <button 
                        onClick={() => setIsEditing(true)}
                        className="flex items-center space-x-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
                    >
                        <Edit2 className="w-4 h-4" />
                        <span>Edit Profile</span>
                    </button>
                )}
            </div>
            
            <div className="space-y-6">
                {/* Profile Card */}
                <form onSubmit={handleUpdateProfile} className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
                    <div className="flex items-center space-x-4">
                        <div className="w-20 h-20 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                            <User className="w-10 h-10" />
                        </div>
                        <div>
                                    <div className="space-y-1">
                                        <p className="text-2xl font-bold text-gray-900">{user.full_name}</p>
                                        <p className="text-[10px] text-gray-400 italic">(Name cannot be changed here)</p>
                                        <p className="text-gray-500 capitalize leading-relaxed">{user.role || 'Customer'}</p>
                                    </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t font-ist">
                        <div className="flex items-start space-x-3">
                            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-sm text-gray-500">Email Address</p>
                                <p className="font-medium text-gray-400">{user.email}</p>
                                <p className="text-[10px] text-gray-400 mt-1 italic">(Email cannot be changed)</p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-3">
                            <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-gray-500">Phone Number</p>
                                {isEditing ? (
                                    <input 
                                        type="tel"
                                        className="font-medium border-b border-gray-200 focus:border-primary-500 outline-none w-full bg-transparent mt-1"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        placeholder="Add phone number"
                                    />
                                ) : (
                                    <p className="font-medium">{user.phone || 'Not added'}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-start space-x-3">
                            <Shield className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-sm text-gray-500">Account Role</p>
                                <p className="font-medium capitalize">{user.role || 'Customer'}</p>
                            </div>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="flex space-x-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-semibold shadow-lg shadow-primary-100 disabled:opacity-50"
                            >
                                <Save className="w-5 h-5" />
                                <span>{loading ? <Loading mode="button" message="Saving..." /> : 'Save Changes'}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setFormData({fullName: user.full_name, phone: user.phone});
                                }}
                                className="px-6 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </form>

                {/* Security Section */}
                {!isEditing && (
                    <div className="bg-white rounded-2xl shadow-sm border p-6">
                        <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                            <Shield className="w-5 h-5 text-primary-600" />
                            <span>Security & Account</span>
                        </h3>
                        <div className="space-y-4">
                            <button
                                onClick={handlePasswordUpdateReq}
                                disabled={loading}
                                className="w-full flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-all text-left group disabled:opacity-50"
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-primary-50 text-primary-600 rounded-lg group-hover:bg-primary-100 transition-colors">
                                        <Key className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">Change Password</p>
                                        <p className="text-xs text-gray-500">Update your account password with OTP</p>
                                    </div>
                                </div>
                                <div className="text-primary-600 font-bold">→</div>
                            </button>

                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center space-x-2 px-6 py-4 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-semibold"
                            >
                                <LogOut className="w-5 h-5" />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
